class HappyCustomers extends HTMLElement {
  static sourceIcons = {
    facebook: 'https://cdn.shopify.com/s/files/1/0756/6393/3752/files/facebook_1.svg?v=1766389234',
    instagram: 'https://cdn.shopify.com/s/files/1/0756/6393/3752/files/instagram.svg?v=1766389235',
    tiktok: 'https://cdn.shopify.com/s/files/1/0756/6393/3752/files/tiktoklogo_tiktok.svg?v=1766389243',
    google: 'https://cdn.shopify.com/s/files/1/0756/6393/3752/files/google_g_logo.svg?v=1766389234'
  };

  constructor() {
    super();
    this.items = [];
    this.config = {};
    this.currentIndex = 1;
    this.desktopOffset = 0;
    this.isPaused = false;
    this.rafId = null;
    this.lastFrame = null;
    this.resizeObserver = null;
    this.videoObserver = null;
    this.playbackObserver = null;
    this.sectionObserver = null;
    this.abortController = null;
    this.modalItem = null;
    this.layoutWidth = 0;
    this.resizeRafId = null;
    this.isSectionVisible = true;
    this.handleResize = this.handleResize.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.animateDesktop = this.animateDesktop.bind(this);
  }

  connectedCallback() {
    this.config = this.readConfig();
    this.root = this.querySelector('[data-happy-customers-root]');
    this.abortController = new AbortController();
    this.isMobile = this.matchesMobile();
    this.layoutWidth = this.getLayoutWidth();
    this.observeSectionVisibility();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.loadItems()
      .then((items) => {
        this.items = items.filter((item) => item && item.url);
        this.render();
        this.observeSize();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.renderError(error);
      });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }
    this.stopDesktopAnimation();
    this.videoObserver?.disconnect();
    this.playbackObserver?.disconnect();
    this.sectionObserver?.disconnect();
    this.unlockPage();
  }

  readConfig() {
    const script = this.querySelector('[data-happy-customers-config]');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('Happy customers config is invalid JSON.', error);
      return {};
    }
  }

  async loadItems() {
    if (Array.isArray(this.config.items) && this.config.items.length > 0) {
      return this.config.items;
    }

    if (!this.config.endpoint) return [];

    const response = await fetch(this.config.endpoint, {
      headers: { Accept: 'application/json' },
      signal: this.abortController?.signal
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch happy customers data: ${response.status}`);
    }

    const data = await response.json();
    if (!Array.isArray(data)) {
      throw new Error('Happy customers data must be an array.');
    }

    return data;
  }

  observeSize() {
    window.addEventListener('resize', this.handleResize);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this);
    }
  }

  handleResize() {
    if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);

    this.resizeRafId = requestAnimationFrame(() => {
      this.resizeRafId = null;
      const wasMobile = this.isMobile;
      const nextMobile = this.matchesMobile();
      const nextWidth = this.getLayoutWidth();
      const widthChanged = Math.abs(nextWidth - this.layoutWidth) > 8;

      this.isMobile = nextMobile;

      if (wasMobile !== nextMobile || widthChanged) {
        this.render();
      } else {
        this.syncVideoPlayback();
      }
    });
  }

  matchesMobile() {
    return window.matchMedia('(max-width: 639px)').matches;
  }

  getLayoutWidth() {
    return Math.round(this.getBoundingClientRect().width || this.clientWidth || window.innerWidth || 0);
  }

  render() {
    if (!this.root) return;

    this.stopDesktopAnimation();
    this.videoObserver?.disconnect();
    this.videoObserver = null;
    this.playbackObserver?.disconnect();
    this.playbackObserver = null;
    this.isMobile = this.matchesMobile();
    this.layoutWidth = this.getLayoutWidth();

    if (!this.items.length) {
      this.root.innerHTML = '<div class="happy-customers__empty">No customer media configured.</div>';
      return;
    }

    this.root.innerHTML = '';

    if (this.config.showHeading !== false) {
      this.root.appendChild(this.createHeader());
    }

    if (this.isMobile) {
      this.root.appendChild(this.createMobileCarousel());
    } else {
      this.root.appendChild(this.createDesktopCarousel());
      this.startDesktopAnimation();
    }

    if (this.modalItem) {
      const modal = this.createModal(this.modalItem);
      modal.dataset.sectionId = this.dataset.sectionId || '';
      document.body.appendChild(modal);
    }

    this.queueVideoPlaybackSync();
  }

  createHeader() {
    const header = document.createElement('header');
    header.className = 'happy-customers__header';

    const heading = document.createElement('h2');
    heading.className = 'happy-customers__heading';
    heading.textContent = this.config.heading || '18000+ Happy Customers';
    header.appendChild(heading);

    if (this.config.subheading) {
      const subheading = document.createElement('p');
      subheading.className = 'happy-customers__subheading';
      subheading.textContent = this.config.subheading;
      header.appendChild(subheading);
    }

    return header;
  }

  createDesktopCarousel() {
    const desktop = document.createElement('div');
    desktop.className = 'happy-customers__desktop';

    const viewport = document.createElement('div');
    viewport.className = 'happy-customers__viewport';

    const track = document.createElement('div');
    track.className = 'happy-customers__track';
    track.style.gap = `${this.desktopGap}px`;

    [...this.items, ...this.items].forEach((item, index) => {
      const cardWrap = document.createElement('div');
      cardWrap.className = 'happy-customers__desktop-card';
      cardWrap.style.width = `${this.desktopCardWidth}px`;
      cardWrap.appendChild(this.createCard(item, { priority: index < 10, active: true, modalTrigger: true }));
      track.appendChild(cardWrap);
    });

    this.track = track;
    viewport.appendChild(track);
    desktop.appendChild(this.createNavButton('prev', () => this.moveDesktop(-1)));
    desktop.appendChild(this.createNavButton('next', () => this.moveDesktop(1)));
    desktop.appendChild(viewport);
    desktop.addEventListener('mouseenter', () => {
      this.isPaused = true;
    });
    desktop.addEventListener('mouseleave', () => {
      this.isPaused = false;
      this.lastFrame = null;
    });

    return desktop;
  }

  createMobileCarousel() {
    const carousel = document.createElement('div');
    carousel.className = 'happy-customers__mobile';
    carousel.style.setProperty('--mobile-card-percent', `${this.mobileCardPercent}%`);

    const viewport = document.createElement('div');
    viewport.className = 'happy-customers__viewport';

    const track = document.createElement('div');
    track.className = 'happy-customers__mobile-track';
    const carouselWidth = this.layoutWidth || this.getLayoutWidth();
    const cardWidth = Math.round(carouselWidth * (this.mobileCardPercent / 100));
    const sideGap = Math.max((carouselWidth - cardWidth) / 2, 0);
    const baseTranslate = sideGap - cardWidth;
    track.style.transform = `translateX(${baseTranslate}px)`;

    this.visibleMobileItems().forEach(({ item, offset }) => {
      const cardWrap = document.createElement('div');
      cardWrap.className = `happy-customers__mobile-card${offset === 0 ? ' is-active' : ''}`;
      cardWrap.style.flex = `0 0 ${cardWidth}px`;
      cardWrap.appendChild(this.createCard(item, {
        active: offset === 0,
        priority: offset === 0,
        modalTrigger: offset === 0,
        useMobileVideo: true
      }));
      track.appendChild(cardWrap);
    });

    viewport.appendChild(track);
    if (this.items.length > 1) {
      carousel.appendChild(this.createNavButton('prev', () => this.moveMobile(-1)));
      carousel.appendChild(this.createNavButton('next', () => this.moveMobile(1)));
    }
    carousel.appendChild(viewport);
    this.attachMobileSwipe(viewport, track, baseTranslate);

    return carousel;
  }

  attachMobileSwipe(viewport, track, baseTranslate) {
    if (this.items.length < 2) return;

    let startX = 0;
    let startY = 0;
    let delta = 0;
    let axis = null;
    let isDragging = false;
    const threshold = 40;

    const onStart = (event) => {
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      delta = 0;
      axis = null;
      isDragging = false;
      track.style.transition = 'none';
    };

    const onMove = (event) => {
      if (axis === 'y') return;
      const touch = event.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!axis) {
        if (absX < 10 && absY < 10) return;
        axis = absX > absY * 1.15 ? 'x' : 'y';
        if (axis === 'y') {
          track.style.transition = '';
          return;
        }
      }

      if (axis === 'x') {
        event.preventDefault();
        isDragging = true;
        delta = dx;
        track.style.transform = `translateX(${baseTranslate + dx}px)`;
      }
    };

    const onEnd = () => {
      track.style.transition = '';
      if (axis === 'x' && isDragging && Math.abs(delta) > threshold) {
        this.moveMobile(delta < 0 ? 1 : -1);
      } else {
        track.style.transform = `translateX(${baseTranslate}px)`;
        this.syncVideoPlayback();
      }
    };

    viewport.addEventListener('touchstart', onStart, { passive: true });
    viewport.addEventListener('touchmove', onMove, { passive: false });
    viewport.addEventListener('touchend', onEnd);
    viewport.addEventListener('touchcancel', onEnd);
  }

  visibleMobileItems() {
    if (this.items.length === 1) {
      return [{ item: this.items[0], offset: 0 }];
    }

    return [-1, 0, 1].map((offset) => {
      let index = this.currentIndex + offset;
      if (index < 0) index = this.items.length + index;
      if (index >= this.items.length) index %= this.items.length;
      return { item: this.items[index], offset };
    });
  }

  createCard(item, options = {}) {
    const card = document.createElement('article');
    card.className = 'happy-customers__card';

    const media = document.createElement('div');
    media.className = 'happy-customers__media';

    if (item.type === 'video') {
      this.appendVideo(media, item, options);
    } else {
      media.appendChild(this.createImage(item.url, item.description || '', options.priority));
    }

    media.appendChild(this.createBadge(item.source));

    if (options.modalTrigger) {
      media.appendChild(this.createExpandButton((event) => {
        event.stopPropagation();
        this.openModal(item);
      }));
    }

    if (this.normalizeSource(item.source) === 'google') {
      media.appendChild(this.createReviewOverlay(item));
    }

    const gradient = document.createElement('div');
    gradient.className = 'happy-customers__gradient';
    media.appendChild(gradient);

    card.appendChild(media);
    return card;
  }

  appendVideo(media, item, options) {
    if (item.poster) {
      media.appendChild(this.createImage(item.poster, '', options.priority));
    }

    if (options.active === false) {
      media.appendChild(this.createPlayOverlay());
      return;
    }

    const video = document.createElement('video');
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'none';
    video.poster = item.poster || '';

    const sourceUrl = options.useMobileVideo ? item.mobileurl || item.url : item.url;
    video.dataset.sourceUrl = sourceUrl;

    video.addEventListener('canplay', () => {
      video.classList.add('is-ready');
      this.safePlayVideo(video);
    });

    video.addEventListener('error', () => {
      video.classList.add('is-ready');
    });

    video.dataset.src = sourceUrl;
    this.observeVideo(video);

    media.appendChild(video);
    this.observePlayback(video);
  }

  observeVideo(video) {
    if (!('IntersectionObserver' in window)) {
      this.setVideoSource(video, video.dataset.src);
      return;
    }

    if (!this.videoObserver) {
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          this.setVideoSource(target, target.dataset.src);
          this.videoObserver?.unobserve(target);
        });
      }, { rootMargin: '300px 0px' });
    }

    this.videoObserver.observe(video);
  }

  setVideoSource(video, src) {
    if (!src || video.querySelector('source')) return;

    const source = document.createElement('source');
    source.src = src;
    source.type = 'video/mp4';
    video.appendChild(source);
    video.load();
    this.safePlayVideo(video);
  }

  observePlayback(video) {
    if (!('IntersectionObserver' in window)) {
      this.safePlayVideo(video);
      return;
    }

    if (!this.playbackObserver) {
      this.playbackObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const target = entry.target;
          if (entry.isIntersecting && this.isSectionVisible && document.visibilityState !== 'hidden') {
            this.safePlayVideo(target);
          } else {
            target.pause();
          }
        });
      }, { rootMargin: '120px 0px', threshold: 0.2 });
    }

    this.playbackObserver.observe(video);
  }

  safePlayVideo(video) {
    if (!video || document.visibilityState === 'hidden' || !this.isSectionVisible) return;

    const sourceUrl = video.dataset.sourceUrl || video.dataset.src;
    if (sourceUrl && !video.querySelector('source')) {
      this.setVideoSource(video, sourceUrl);
      return;
    }

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  pauseAllVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  }

  syncVideoPlayback() {
    if (!this.isSectionVisible || document.visibilityState === 'hidden') {
      this.pauseAllVideos();
      return;
    }

    this.querySelectorAll('video').forEach((video) => {
      if (this.isElementVisible(video)) {
        this.safePlayVideo(video);
      } else {
        video.pause();
      }
    });
  }

  queueVideoPlaybackSync() {
    requestAnimationFrame(() => this.syncVideoPlayback());
  }

  isElementVisible(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    return rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth;
  }

  observeSectionVisibility() {
    if (!('IntersectionObserver' in window)) return;

    this.sectionObserver?.disconnect();
    this.sectionObserver = new IntersectionObserver((entries) => {
      const isVisible = entries.some((entry) => entry.isIntersecting);
      this.isSectionVisible = isVisible;

      if (isVisible) {
        this.syncVideoPlayback();
      } else {
        this.pauseAllVideos();
      }
    }, { rootMargin: '160px 0px', threshold: 0.01 });

    this.sectionObserver.observe(this);
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.pauseAllVideos();
    } else {
      this.syncVideoPlayback();
    }
  }

  createImage(src, alt, priority) {
    const image = document.createElement('img');
    image.src = src;
    image.alt = alt;
    image.loading = priority ? 'eager' : 'lazy';
    image.decoding = 'async';
    return image;
  }

  createBadge(source) {
    const key = this.normalizeSource(source);
    const badge = document.createElement('div');
    badge.className = 'happy-customers__badge';
    badge.title = key || '';

    if (!key) return badge;

    const icon = HappyCustomers.sourceIcons[key];
    if (icon) {
      const image = document.createElement('img');
      image.src = icon;
      image.alt = '';
      image.loading = 'lazy';
      badge.appendChild(image);
    }

    const text = document.createElement('span');
    text.textContent = `Powered By ${this.titleCase(key)}`;
    badge.appendChild(text);
    return badge;
  }

  createReviewOverlay(item) {
    const fragment = document.createDocumentFragment();
    const gradient = document.createElement('div');
    gradient.className = 'happy-customers__review-gradient';
    fragment.appendChild(gradient);

    const review = document.createElement('div');
    review.className = 'happy-customers__review';

    const reviewer = document.createElement('div');
    reviewer.className = 'happy-customers__reviewer';

    const avatar = document.createElement('div');
    avatar.className = 'happy-customers__avatar';
    if (item.avatar) {
      avatar.appendChild(this.createImage(item.avatar, '', false));
    }

    const name = document.createElement('span');
    name.className = 'happy-customers__name';
    name.textContent = item.username || 'Happy Customer';

    reviewer.appendChild(avatar);
    reviewer.appendChild(name);

    const comment = document.createElement('p');
    comment.className = 'happy-customers__comment';
    comment.textContent = item.comment || item.description || '';

    review.appendChild(reviewer);
    review.appendChild(comment);
    fragment.appendChild(review);
    return fragment;
  }

  createPlayOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'happy-customers__play';
    overlay.innerHTML = '<svg width="24" height="24" fill="#333" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"></path></svg>';
    return overlay;
  }

  createExpandButton(onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'happy-customers__expand';
    button.setAttribute('aria-label', 'View full screen');
    button.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 3H4a1 1 0 0 0-1 1v4M16 3h4a1 1 0 0 1 1 1v4M21 16v4a1 1 0 0 1-1 1h-4M8 21H4a1 1 0 0 1-1-1v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M9 9 4 4M15 9l5-5M15 15l5 5M9 15l-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>';
    button.addEventListener('click', onClick);
    return button;
  }

  createNavButton(direction, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `happy-customers__nav happy-customers__nav--${direction}`;
    button.setAttribute('aria-label', direction === 'prev' ? 'Previous' : 'Next');
    button.innerHTML = direction === 'prev'
      ? '<svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>'
      : '<svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>';
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      onClick();
    });
    return button;
  }

  createModal(item) {
    const modal = document.createElement('div');
    modal.className = 'happy-customers__modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.addEventListener('click', () => this.closeModal());

    const dialog = document.createElement('div');
    dialog.className = 'happy-customers__dialog';
    dialog.addEventListener('click', (event) => event.stopPropagation());

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'happy-customers__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>';
    close.addEventListener('click', () => this.closeModal());

    const media = document.createElement('div');
    media.className = 'happy-customers__modal-media';

    if (item.type === 'video') {
      const video = document.createElement('video');
      video.src = this.isMobile ? item.mobileurl || item.url : item.url;
      video.poster = item.poster || '';
      video.controls = true;
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      media.appendChild(video);
    } else {
      media.appendChild(this.createImage(item.url, item.description || '', true));
    }

    const badgeWrap = document.createElement('div');
    badgeWrap.style.position = 'absolute';
    badgeWrap.style.top = '12px';
    badgeWrap.style.left = '12px';
    badgeWrap.style.zIndex = '10';
    badgeWrap.appendChild(this.createBadge(item.source));
    media.appendChild(badgeWrap);

    dialog.appendChild(close);
    dialog.appendChild(media);

    const body = this.createModalBody(item);
    if (body) dialog.appendChild(body);

    modal.appendChild(dialog);
    return modal;
  }

  createModalBody(item) {
    const source = this.normalizeSource(item.source);
    const body = document.createElement('div');
    body.className = 'happy-customers__modal-body';

    if (source === 'google') {
      const reviewer = document.createElement('div');
      reviewer.className = 'happy-customers__reviewer';

      const avatar = document.createElement('div');
      avatar.className = 'happy-customers__avatar';
      if (item.avatar) {
        avatar.appendChild(this.createImage(item.avatar, '', true));
      }

      const details = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'happy-customers__name';
      name.textContent = item.username || 'Happy Customer';

      const sourceLabel = document.createElement('div');
      sourceLabel.className = 'happy-customers__modal-source';
      sourceLabel.textContent = 'Google Review';

      details.appendChild(name);
      details.appendChild(sourceLabel);
      reviewer.appendChild(avatar);
      reviewer.appendChild(details);

      const comment = document.createElement('p');
      comment.className = 'happy-customers__comment';
      comment.textContent = item.comment || item.description || '';

      body.appendChild(reviewer);
      body.appendChild(comment);
      return body;
    }

    if (item.description) {
      const description = document.createElement('p');
      description.className = 'happy-customers__comment';
      description.textContent = item.description;
      body.appendChild(description);
      return body;
    }

    return null;
  }

  openModal(item) {
    this.modalItem = item;
    this.lockPage();
    document.querySelectorAll(`.happy-customers__modal[data-section-id="${this.dataset.sectionId}"]`).forEach((modal) => modal.remove());
    const modal = this.createModal(item);
    modal.dataset.sectionId = this.dataset.sectionId || '';
    document.body.appendChild(modal);
  }

  closeModal() {
    this.modalItem = null;
    document.querySelectorAll(`.happy-customers__modal[data-section-id="${this.dataset.sectionId}"]`).forEach((modal) => modal.remove());
    this.unlockPage();
  }

  lockPage() {
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  unlockPage() {
    if (this.previousOverflow !== undefined) {
      document.body.style.overflow = this.previousOverflow;
    }
  }

  moveMobile(direction) {
    if (!this.items.length) return;
    this.currentIndex += direction;
    if (this.currentIndex < 0) this.currentIndex = this.items.length - 1;
    if (this.currentIndex >= this.items.length) this.currentIndex = 0;
    this.render();
  }

  moveDesktop(direction) {
    const amount = (this.desktopCardWidth + this.desktopGap) * 4 * direction;
    this.desktopOffset = Math.max(0, this.desktopOffset + amount);
    this.applyDesktopOffset();
  }

  startDesktopAnimation() {
    this.lastFrame = null;
    this.rafId = requestAnimationFrame(this.animateDesktop);
  }

  stopDesktopAnimation() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  animateDesktop(timestamp) {
    if (!this.track || this.items.length === 0) return;

    if (!this.isPaused) {
      if (!this.lastFrame) this.lastFrame = timestamp;
      const delta = Math.min(timestamp - this.lastFrame, 50);
      this.lastFrame = timestamp;
      this.desktopOffset += this.desktopSpeed * delta;

      if (this.desktopOffset >= this.loopWidth) {
        this.desktopOffset = 0;
      }

      this.applyDesktopOffset();
    }

    this.rafId = requestAnimationFrame(this.animateDesktop);
  }

  applyDesktopOffset() {
    if (!this.track) return;

    if (this.desktopOffset >= this.loopWidth) {
      this.desktopOffset = 0;
    }

    this.track.style.transform = `translateX(-${this.desktopOffset}px)`;
  }

  renderError(error) {
    if (!this.root) return;
    const message = error?.message || String(error);
    this.root.innerHTML = `<div class="happy-customers__error">Unable to load customer media: ${this.escapeHtml(message)}</div>`;
  }

  normalizeSource(source) {
    const value = String(source || '').trim().toLowerCase();
    if (value === 'fb') return 'facebook';
    if (value === 'ins' || value === 'ig') return 'instagram';
    if (value === 'tt') return 'tiktok';
    if (value === 'gg') return 'google';
    return value;
  }

  titleCase(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[char]);
  }

  get desktopCardWidth() {
    return Number(this.config.desktopCardWidth) || 240;
  }

  get desktopGap() {
    return Number(this.config.desktopGap) || 12;
  }

  get desktopSpeed() {
    return (Number(this.config.autoplaySpeed) || 30) / 1000;
  }

  get mobileCardPercent() {
    return Number(this.config.mobileCardPercent) || 70;
  }

  get loopWidth() {
    return this.items.length * (this.desktopCardWidth + this.desktopGap);
  }
}

if (!customElements.get('happy-customers')) {
  customElements.define('happy-customers', HappyCustomers);
}
